import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2 } from "lucide-react";

let _openStepUp = null;

export function isStepUpRequired(err) {
    const det = err?.response?.data?.detail;
    return (
        err?.response?.status === 401 &&
        det &&
        typeof det === "object" &&
        det.step_up_required === true
    );
}

/**
 * Run an API call; if the backend answers 401 {step_up_required}, prompt the
 * user for a fresh MFA code, then retry once with the X-Step-Up-Token header.
 * requestFn receives an extra-headers object. Resolves null if the user cancels.
 */
export async function callWithStepUp(requestFn) {
    try {
        return await requestFn({});
    } catch (err) {
        if (!isStepUpRequired(err) || !_openStepUp) throw err;
        const token = await new Promise((resolve) => _openStepUp(resolve));
        if (!token) {
            const cancelled = new Error("step-up-cancelled");
            cancelled.stepUpCancelled = true;
            throw cancelled;
        }
        return await requestFn({ "X-Step-Up-Token": token });
    }
}

/** Mounted once in App.js — a global prompt for step-up MFA verification. */
export default function StepUpDialog() {
    const { user } = useAuth();
    const usesMfa = !!user?.mfa_enrolled;
    const [resolver, setResolver] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        _openStepUp = (resolve) => {
            setCode("");
            setErr("");
            setResolver(() => resolve);
        };
        return () => {
            _openStepUp = null;
        };
    }, []);

    const cancel = () => {
        resolver?.(null);
        setResolver(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/auth/mfa/step-up", {
                code: code.trim(),
            });
            const resolve = resolver;
            setResolver(null);
            resolve?.(data.step_up_token);
        } catch (e2) {
            setErr(formatApiError(e2?.response?.data?.detail) || "Incorrect code");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!resolver} onOpenChange={(o) => { if (!o) cancel(); }}>
            <DialogContent className="sm:max-w-md bg-card border-border" data-testid="step-up-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
                        Verify It&#39;s You
                    </DialogTitle>
                    <DialogDescription>
                        This is a sensitive action. Enter the 6-digit code from
                        your authenticator (or a recovery code) to continue.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <Label htmlFor="step-up-code" className="mono-label">
                        {usesMfa ? "Verification code" : "Account password"}
                    </Label>
                    <Input
                        id="step-up-code"
                        autoFocus
                        type={usesMfa ? "text" : "password"}
                        inputMode={usesMfa ? "numeric" : "text"}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className={usesMfa ? "tracking-widest font-mono text-lg" : ""}
                        placeholder={usesMfa ? "123456" : "••••••••"}
                        data-testid="step-up-code-input"
                    />
                    {err && (
                        <div className="text-sm text-destructive" data-testid="step-up-error">
                            {err}
                        </div>
                    )}
                    <div className="flex gap-3 pt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={cancel}
                            className="rounded-md font-display"
                            data-testid="step-up-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={busy || (usesMfa ? code.trim().length < 6 : code.length < 1)}
                            className="rounded-md font-display flex-1"
                            data-testid="step-up-submit"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} />
                            ) : (
                                <ShieldCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                            )}
                            Verify & continue
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
